/**
 * Templates (precedents) API client — aligned with FE technical spec v1.1.
 *
 * Two principles encoded here:
 *
 *  - **No detail endpoint.** The list-row entry IS the detail. Mutations
 *    return `200` with the same `DocumentTemplateEntry` shape — including
 *    the idempotent cases (set-default on the current default, restore the
 *    current version, no-op rename). No `204` no-ops.
 *  - **Vale standards are addressed by `systemKey`, not by id.** Firm rows
 *    have a numeric `id`; system entries carry `systemKey` and `id: null`.
 */

import { directApiClient } from '@/lib/api/direct-client';
import type {
  ArchiveTemplateRequest,
  CommitTemplateRequest,
  DocumentTemplateEntry,
  DocumentTemplateHistoryEvent,
  DocumentTemplateVersion,
  DocumentType,
  EditInWordRequest,
  EditInWordResponse,
  MergeFieldsRegistry,
  ReplaceContentRequest,
  UploadPreviewResponse
} from './types';

const BASE = '/templates/documents';
const DOWNLOAD_BASE = `/api/proxy${BASE}`;

// ---------------------------------------------------------------------------
// Upload helper — step 1 of the three-step flow
// ---------------------------------------------------------------------------

interface TemplatePresignedUrlResponse {
  presignedUrl: string;
  fileId: number;
}

/**
 * Request a presigned URL for a template upload.
 *
 * `POST /files/pre-signed-url` with `{ fileType: 'document-template' }`.
 * Backend creates a PENDING `files` row, returns the S3 URL (the FE PUTs the
 * bytes) and a `fileId` to feed into the templates commit endpoint, which
 * internally calls `FilesService.completeUploadWithAssociations` — no
 * separate `/upload-complete` call from the FE.
 */
export async function requestTemplatePresignedUrl(): Promise<TemplatePresignedUrlResponse> {
  return directApiClient.post<TemplatePresignedUrlResponse>(
    '/files/pre-signed-url',
    { fileType: 'document-template' }
  );
}

/**
 * End-to-end upload helper: presigned URL → S3 PUT → returns the backend
 * `fileId` ready for step 2 (`/templates/documents/preview`).
 */
export async function uploadTemplateFile(file: File): Promise<number> {
  const { presignedUrl, fileId } = await requestTemplatePresignedUrl();
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' }
  });
  if (!response.ok) {
    throw new Error(`Failed to upload to storage (HTTP ${response.status}).`);
  }
  return fileId;
}

// ---------------------------------------------------------------------------
// Library
// ---------------------------------------------------------------------------

export async function fetchActiveTemplates(
  documentType?: DocumentType
): Promise<DocumentTemplateEntry[]> {
  return directApiClient.get<DocumentTemplateEntry[]>(BASE, {
    params: documentType ? { documentType } : undefined
  });
}

export async function fetchArchivedTemplates(
  documentType?: DocumentType
): Promise<DocumentTemplateEntry[]> {
  return directApiClient.get<DocumentTemplateEntry[]>(`${BASE}/archived`, {
    params: documentType ? { documentType } : undefined
  });
}

// ---------------------------------------------------------------------------
// Preview / download
// ---------------------------------------------------------------------------

export async function renderFirmPreview(
  id: number
): Promise<UploadPreviewResponse> {
  return directApiClient.post<UploadPreviewResponse>(`${BASE}/${id}/preview`);
}

export async function renderSystemPreview(
  systemKey: string
): Promise<UploadPreviewResponse> {
  return directApiClient.post<UploadPreviewResponse>(
    `${BASE}/system/${encodeURIComponent(systemKey)}/preview`
  );
}

export function getFirmDownloadUrl(id: number): string {
  return `${DOWNLOAD_BASE}/${id}/download`;
}

export function getSystemDownloadUrl(systemKey: string): string {
  return `${DOWNLOAD_BASE}/system/${encodeURIComponent(systemKey)}/download`;
}

// ---------------------------------------------------------------------------
// Upload step 2 + 3
// ---------------------------------------------------------------------------

export async function previewUploadedTemplate(
  fileId: number,
  documentType: DocumentType,
  // The office this upload will belong to, so a letterhead's `<<office.*>>`
  // merge fields preview that office's real details rather than sample ones.
  // Omitted (a firm-level upload) previews firm-level.
  officeId?: number
): Promise<UploadPreviewResponse> {
  return directApiClient.post<UploadPreviewResponse>(`${BASE}/preview`, {
    fileId,
    documentType,
    officeId
  });
}

export async function commitTemplate(
  data: CommitTemplateRequest
): Promise<DocumentTemplateEntry> {
  return directApiClient.post<DocumentTemplateEntry>(BASE, data);
}

// ---------------------------------------------------------------------------
// Lifecycle — all return 200 with the entry shape (no 204 no-ops)
// ---------------------------------------------------------------------------

export async function renameTemplate(
  id: number,
  name: string
): Promise<DocumentTemplateEntry> {
  return directApiClient.patch<DocumentTemplateEntry>(`${BASE}/${id}`, {
    name
  });
}

export async function replaceTemplateContent(
  id: number,
  data: ReplaceContentRequest
): Promise<DocumentTemplateEntry> {
  return directApiClient.put<DocumentTemplateEntry>(
    `${BASE}/${id}/content`,
    data
  );
}

export async function setTemplateDefault(
  id: number
): Promise<DocumentTemplateEntry> {
  return directApiClient.post<DocumentTemplateEntry>(
    `${BASE}/${id}/set-default`
  );
}

export async function archiveTemplate(
  id: number,
  data: ArchiveTemplateRequest = {}
): Promise<DocumentTemplateEntry> {
  return directApiClient.delete<DocumentTemplateEntry>(`${BASE}/${id}`, data);
}

export async function unarchiveTemplate(
  id: number
): Promise<DocumentTemplateEntry> {
  return directApiClient.post<DocumentTemplateEntry>(`${BASE}/${id}/unarchive`);
}

// ---------------------------------------------------------------------------
// Versions / history
// ---------------------------------------------------------------------------

export async function fetchTemplateVersions(
  id: number
): Promise<DocumentTemplateVersion[]> {
  return directApiClient.get<DocumentTemplateVersion[]>(
    `${BASE}/${id}/versions`
  );
}

export function getVersionDownloadUrl(id: number, versionId: number): string {
  return `${DOWNLOAD_BASE}/${id}/versions/${versionId}/download`;
}

export async function restoreTemplateVersion(
  id: number,
  versionId: number
): Promise<DocumentTemplateEntry> {
  return directApiClient.post<DocumentTemplateEntry>(
    `${BASE}/${id}/versions/${versionId}/restore`
  );
}

export async function fetchTemplateHistory(
  id: number
): Promise<DocumentTemplateHistoryEvent[]> {
  return directApiClient.get<DocumentTemplateHistoryEvent[]>(
    `${BASE}/${id}/history`
  );
}

// ---------------------------------------------------------------------------
// Merge fields
// ---------------------------------------------------------------------------

export async function fetchMergeFieldsRegistry(
  documentType: DocumentType
): Promise<MergeFieldsRegistry> {
  return directApiClient.get<MergeFieldsRegistry>(
    `${BASE}/merge-fields/${documentType}`
  );
}

export function getMergeFieldsReferenceUrl(documentType: DocumentType): string {
  return `${DOWNLOAD_BASE}/merge-fields/${documentType}/reference-docx/download`;
}

// ---------------------------------------------------------------------------
// Edit in Word (provision-on-edit) — LF-5224 / BE-11
// ---------------------------------------------------------------------------

/**
 * Provision a working copy in the firm's SPE container and return the
 * Office-for-web `webUrl` to open in a new tab. A `systemKey` request
 * fork-first-copies the Vale standard to a firm template; a `templateId`
 * request opens the firm row's working copy directly.
 */
export async function editInWord(
  data: EditInWordRequest
): Promise<EditInWordResponse> {
  return directApiClient.post<EditInWordResponse>(`${BASE}/edit-in-word`, data);
}
